import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Avatar,
    Typography,
    Box,
    Collapse,
    useMediaQuery,
    useTheme,
    IconButton,
} from '@mui/material';
import {
    ExpandLess,
    ExpandMore,
    ChevronLeft,
} from '@mui/icons-material';
import logo from '../images/logo.png';
import SidebarContext from './sidebarcontext/SidebarContext';
import AuthContext from './auth-context';
import { menuItems } from './MenuItems';

export default function Sidebarone() {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const context = useContext(SidebarContext);
    const { menutoggle, setMenutoggle, setAlert, logoutdata, userinfo } = context;

    const [openMenus, setOpenMenus] = useState({});
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const location = useLocation();

    const handleDrawerClose = () => {
        if (isSmallScreen) setMenutoggle(true);
    };

    const openInNewTab = (url) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
    };

    function logOut(e) {
        e.preventDefault();
        setAlert({
            fade: 'fade-in',
            msg: "Do you want to logout?",
            type: "logout"
        });
    }

    function logoutFunction() {
        authCtx.logout();
        localStorage.clear();
        setAlert({ fade: 'fade-default', msg: '', type: '' });
        navigate("/login");
    }

    useEffect(() => {
        if (logoutdata) logoutFunction();
    }, [logoutdata]);

    // Fixed: Make sure ChangePW always receives an event or handles no event
    function ChangePW(e) {
        // Prevent default only if event exists
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        setAlert({ fade: "fade-in", msg: "", type: "change" });
    }

    const handleMenuToggle = (label, e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const isSelected = (path) => location.pathname === path;

    useEffect(() => {
        const newOpenMenus = {};
        menuItems.forEach(item => {
            if (item.children) {
                const hasSelectedChild = item.children.some(child => isSelected(child.path));
                if (hasSelectedChild) {
                    newOpenMenus[item.label] = true;
                } else if (openMenus[item.label] === undefined) {
                    newOpenMenus[item.label] = false;
                } else {
                    newOpenMenus[item.label] = openMenus[item.label];
                }
            }
        });
        setOpenMenus(prev => ({ ...prev, ...newOpenMenus }));
    }, [location.pathname]);

    return (
        <Drawer
            variant={isSmallScreen ? "temporary" : "permanent"}
            elevation={2}
            open={!menutoggle}
            onClose={handleDrawerClose}
            sx={{
                width: 240,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: 240,
                    boxSizing: 'border-box',
                    fontFamily: '"Nunito", sans-serif !important', // Changed to Nunito
                    fontWeight: 700, // Added bold weight
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
                <Avatar src={logo} alt="esPay" sx={{ width: 40, height: 40, mr: 1 }} />
                <Typography variant="h6" component="div" sx={{
                    flexGrow: 1,
                    fontSize: '1.25rem',
                    fontWeight: 700, // Increased to bold (700)
                    fontFamily: '"Nunito", sans-serif !important'
                }}>
                    esPay
                </Typography>
                {isSmallScreen && <IconButton onClick={handleDrawerClose}><ChevronLeft /></IconButton>}
            </Box>

            <Divider />

            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ width: 40, height: 40, mr: 2 }} />
                <Box>
                    <Typography variant="subtitle2" sx={{
                        fontSize: '0.875rem',
                        fontWeight: 700, // Increased to bold
                        fontFamily: '"Nunito", sans-serif !important'
                    }}>
                        {userinfo?.customerName}
                    </Typography>
                    <Typography variant="caption" sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700, // Semi-bold
                        fontFamily: '"Nunito", sans-serif !important'
                    }}>
                        {userinfo?.UserName}
                    </Typography>
                </Box>
            </Box>

            <Divider />

            <List sx={{ p: 1, fontFamily: '"Nunito", sans-serif !important' }}>
                {menuItems.map((item, index) => {
                    const Icon = item.icon;
                    const selected = isSelected(item.path) || (item.children ? item.children.some(child => isSelected(child.path)) : false);
                    const isMenuOpen = openMenus[item.label] || false;

                    return (
                        <div key={index}>
                            <ListItem
                                button={!!item.path || !!item.children}
                                component={item.path ? Link : "div"}
                                to={item.path || "#"}
                                onClick={(e) => {
                                    handleDrawerClose();
                                    if (item.children) {
                                        handleMenuToggle(item.label, e);
                                    } else if (item.path) {
                                        const newOpenMenus = {};
                                        menuItems.forEach(i => {
                                            if (i.children && i.label !== item.label) {
                                                newOpenMenus[i.label] = false;
                                            }
                                        });
                                        setOpenMenus(prev => ({ ...prev, ...newOpenMenus }));
                                    }
                                    if (item.action === "changePassword") ChangePW(e);
                                    if (item.externalLink) openInNewTab(item.externalLink);
                                }}
                                sx={{
                                    bgcolor: selected ? theme.palette.grey[100] : 'transparent',
                                    color: selected ? theme.palette.primary.main : 'text.secondary',
                                    '& .MuiListItemIcon-root': {
                                        color: selected ? theme.palette.primary.main : 'text.secondary',
                                        minWidth: '40px',
                                    },
                                    '& .MuiListItemText-primary': {
                                        color: selected ? theme.palette.primary.main : 'text.secondary',
                                        fontSize: '0.85rem',
                                        fontWeight: selected ? 800 : 700, // Increased weights
                                        fontFamily: '"Nunito", sans-serif !important',
                                    },
                                    py: 0.5,
                                    my: 0.25,
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: '40px' }}>
                                    <Icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontSize: '0.85rem',
                                            fontWeight: 700, // Added bold weight
                                            fontFamily: '"Nunito", sans-serif !important'
                                        }
                                    }}
                                />
                                {item.children && (isMenuOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
                            </ListItem>

                            {item.children && (
                                <Collapse in={isMenuOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.children.map((child, childIndex) => {
                                            const childSelected = isSelected(child.path);
                                            const ChildIcon = child.icon || item.icon;

                                            return (
                                                <ListItem
                                                    button
                                                    key={childIndex}
                                                    component={child.path ? Link : "div"}
                                                    to={child.path || "#"}
                                                    onClick={(e) => {
                                                        handleDrawerClose();
                                                        if (child.action === "changePassword") ChangePW(e);
                                                    }}
                                                    sx={{
                                                        pl: 3,
                                                        bgcolor: childSelected ? theme.palette.grey[300] : 'transparent',
                                                        color: childSelected ? theme.palette.primary.main : 'text.secondary',
                                                        '& .MuiListItemIcon-root': {
                                                            color: childSelected ? theme.palette.primary.main : 'text.secondary',
                                                            minWidth: '35px',
                                                        },
                                                        '& .MuiListItemText-primary': {
                                                            color: childSelected ? theme.palette.primary.main : 'text.secondary',
                                                            fontSize: '0.8rem',
                                                            fontWeight: childSelected ? 800 : 700, // Increased weights
                                                            fontFamily: '"Nunito", sans-serif !important'
                                                        },
                                                        py: 0.4,
                                                        my: 0.1,
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: '35px' }}>
                                                        <ChildIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={child.label}
                                                        sx={{
                                                            '& .MuiTypography-root': {
                                                                fontFamily: '"Nunito", sans-serif !important',
                                                                fontWeight: 700,
                                                            }
                                                        }}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            )}
                        </div>
                    );
                })}
            </List>
        </Drawer>
    );
}